import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { ArrowRight, Check, Sparkle } from "@phosphor-icons/react";

export default function Pricing() {
  const { user } = useAuth();
  const nav = useNavigate();

  const plans = [
    {
      id: "free", name: "Free",
      price: "$0", per: "/forever", setup: "",
      feats: ["Up to 50 contacts", "Card scanning (10/mo)", "100 emails/mo", "Digital card + QR", "Manual follow-ups"],
      cta: user ? "Go to dashboard" : "Start free — no card needed",
      onClick: () => nav(user ? "/dashboard" : "/register"),
      primary: false,
      badge: "Try it free",
    },
    {
      id: "starter", name: "Starter",
      price: "Custom", per: "monthly + setup", setup: "priced by scope",
      feats: ["Up to 500 contacts", "50 scans/mo", "1,000 emails/mo", "Email sending (Resend)", "Events tagging", "Referral mode"],
      cta: "Book a call",
      onClick: () => nav("/book-a-call?plan=Starter"),
      primary: false,
    },
    {
      id: "pro", name: "Pro",
      price: "Custom", per: "monthly + setup", setup: "priced by scope",
      feats: ["Unlimited contacts", "Unlimited card scans", "25,000 emails/mo", "AI message drafts", "Auto campaigns", "Email + SMS sending", "Stage-change triggers"],
      cta: "Book a call",
      onClick: () => nav("/book-a-call?plan=Pro"),
      primary: true,
      badge: "most popular",
    },
    {
      id: "team", name: "Team",
      price: "Custom", per: "per-user + setup", setup: "priced by scope",
      feats: ["Everything in Pro", "Unlimited emails", "Shared pipeline", "Team templates", "Manager reporting", "Priority support", "Custom onboarding"],
      cta: "Book a call",
      onClick: () => nav("/book-a-call?plan=Team"),
      primary: false,
    },
  ];

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }} data-testid="pricing-page">
      <header className="border-b sticky top-0 z-30" style={{ borderColor: "var(--border)", background: "rgba(10,20,24,0.85)", backdropFilter: "blur(12px)" }}>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
          <Link to="/" className="font-display font-black text-2xl tracking-tighter">
            MY NEXUS<span style={{ color: "var(--accent)" }}> </span>CARD
          </Link>
          <div className="flex items-center gap-4">
            {user ? (
              <Link to="/dashboard" className="overline hover:text-white">Dashboard →</Link>
            ) : (
              <Link to="/login" className="overline hover:text-white">Sign in</Link>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-24">
        <div className="text-center mb-14">
          <div className="overline mb-4">// pricing</div>
          <h1 className="font-display font-black text-5xl md:text-7xl tracking-tighter leading-none">
            Try free. Then <span style={{ color: "var(--accent)" }}>scope</span> your fit.
          </h1>
          <p className="mt-6 max-w-2xl mx-auto text-lg" style={{ color: "var(--text-2)" }}>
            Start on the Free plan today — no credit card, no expiry. When you're ready to scale, book a 20-minute call and we'll price a setup + monthly plan tailored to your team size, contact volume, and campaign complexity.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border-l border-t" style={{ borderColor: "var(--border)" }}>
          {plans.map((p) => (
            <div key={p.id} className="border-r border-b p-8 flex flex-col"
              style={{ borderColor: p.primary ? "var(--accent)" : "var(--border)", background: p.primary ? "rgba(45,212,212,0.04)" : "transparent" }}
              data-testid={`tier-${p.id}`}>
              <div className="flex items-center justify-between">
                <div className="overline" style={p.primary ? { color: "var(--accent)" } : {}}>{p.name}</div>
                {p.badge && (
                  <span className="tag" style={p.primary ? { color: "var(--accent)", borderColor: "var(--accent)" } : {}}>{p.badge}</span>
                )}
              </div>
              <div className="mt-6 flex items-baseline gap-2">
                <span className="font-display font-black text-5xl tracking-tighter">{p.price}</span>
                <span className="font-mono text-sm" style={{ color: "var(--text-2)" }}>{p.per}</span>
              </div>
              {p.setup && (
                <div className="mt-2 overline" style={{ color: "var(--text-3)" }}>{p.setup}</div>
              )}
              <ul className="mt-6 space-y-3 flex-1">
                {p.feats.map((f) => (
                  <li key={f} className="text-sm flex items-start gap-3">
                    <Check weight="bold" style={{ color: "var(--accent)", flexShrink: 0, marginTop: 2 }} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <button onClick={p.onClick} data-testid={`cta-${p.id}`}
                className={p.primary ? "btn-primary mt-8 w-full flex items-center justify-between" : "btn-secondary mt-8 w-full flex items-center justify-between"}>
                {p.cta} <ArrowRight weight="bold" />
              </button>
            </div>
          ))}
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
          <div className="border p-6" style={{ borderColor: "var(--border)" }}>
            <Sparkle weight="duotone" size={24} color="var(--accent)" />
            <h4 className="font-display font-bold mt-3 text-lg" style={{ color: "var(--text)" }}>Start free forever</h4>
            <p className="mt-2" style={{ color: "var(--text-2)" }}>Sign up and use core features — card scanning, contacts, digital card — with no time limit.</p>
          </div>
          <div className="border p-6" style={{ borderColor: "var(--border)" }}>
            <Check weight="duotone" size={24} color="var(--accent)" />
            <h4 className="font-display font-bold mt-3 text-lg" style={{ color: "var(--text)" }}>Custom quotes</h4>
            <p className="mt-2" style={{ color: "var(--text-2)" }}>Setup and monthly fees are quoted per business — no cookie-cutter tiers. Pay only for what you actually need.</p>
          </div>
          <div className="border p-6" style={{ borderColor: "var(--border)" }}>
            <ArrowRight weight="duotone" size={24} color="var(--accent)" />
            <h4 className="font-display font-bold mt-3 text-lg" style={{ color: "var(--text)" }}>Invoice direct</h4>
            <p className="mt-2" style={{ color: "var(--text-2)" }}>Once we align on scope, invoices are sent directly. No auto-charges, no surprise billing.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
